import { CSSProperties, ElementType, ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Adds `is-in` once the element scrolls into view (one-shot). */
export function useInView<T extends Element>(options: IntersectionObserverInit = { rootMargin: '0px 0px -10% 0px' }) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return { ref, inView };
}

export function Reveal({
  as: Tag = 'div',
  delay = 0,
  className,
  children,
  style,
}: {
  as?: ElementType;
  delay?: number;
  className?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const { ref, inView } = useInView<HTMLElement>();
  return (
    <Tag
      ref={ref}
      className={cn('mk-reveal', inView && 'is-in', className)}
      style={{ ...style, ['--mk-delay' as string]: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('mk-eyebrow', className)}>{children}</span>;
}

export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = 'center',
  className,
  id,
}: {
  /** id for the h2 (for aria-labelledby). */
  id?: string;
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  align?: 'center' | 'left';
  className?: string;
}) {
  return (
    <Reveal className={cn(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl', className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 id={id} className={cn('mk-h2', eyebrow && 'mt-5')}>{title}</h2>
      {lede && <p className="mk-lede mt-5 text-[hsl(var(--mk-muted))]">{lede}</p>}
    </Reveal>
  );
}

type CtaVariant = 'sun' | 'ink' | 'ghost';

export function CtaLink({
  to,
  children,
  variant = 'sun',
  size,
  arrow = true,
  className,
}: {
  to: string;
  children: ReactNode;
  variant?: CtaVariant;
  size?: 'sm';
  arrow?: boolean;
  className?: string;
}) {
  return (
    <Link to={to} className={cn('mk-btn', `mk-btn-${variant}`, size === 'sm' && 'mk-btn-sm', className)}>
      {children}
      {arrow && <ArrowRight className="h-4 w-4" />}
    </Link>
  );
}

/** Standard top-of-page hero for sales sub-pages (features, pricing, about…). */
export function PageHero({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="mk-ink-bg mk-on-dark relative overflow-hidden">
      <div className="container mx-auto px-4 pb-24 pt-[108px] md:pb-28">
        <div className="mx-auto max-w-4xl text-center">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className="mk-display mt-7 text-white">{title}</h1>
          {lede && <p className="mk-lede mx-auto mt-6 max-w-2xl text-white/70">{lede}</p>}
          {children && <div className="mt-10">{children}</div>}
        </div>
      </div>
    </section>
  );
}
